import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, renderSlot as _renderSlot, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["data-uniqueid"]
const _hoisted_2 = {
  key: 0,
  class: "additional-notes"
}
const _hoisted_3 = {
  key: 1,
  class: "noted-colors"
}
const _hoisted_4 = ["src"]

import { ref, computed, getCurrentInstance, useTemplateRef } from "vue";
import { Language } from "../../../src/Types";
import { UniqueCard } from "@/CardTypes";
import { hasEffect, OnPickDraftEffect } from "../../../src/CardTypes";

import CardImage from "./CardImage.vue";

import { useEmitter } from "../appCommon";

export default /*@__PURE__*/_defineComponent({
  __name: 'Card',
  props: {
    card: {},
    language: { default: Language.en },
    lazyLoad: { type: Boolean, default: false },
    renderCommonBackside: { type: Boolean, default: false },
    conditionalClasses: { type: Function, default: undefined }
  },
  setup(__props: any) {

const { emitter } = useEmitter();

const props = __props;

const displayCardText = ref(false);
const rootElement = useTemplateRef("rootElement");
const imageElement = useTemplateRef<InstanceType<typeof CardImage>>("imageElement");

const classes = computed(() => {
	return props.conditionalClasses ? props.conditionalClasses(props.card) : [];
});

const notes = computed(() => {
	if (!props.card?.state) return undefined;
	if (props.card.draft_effects) {
		if (hasEffect(props.card, "TrackRemovedCardsNames") && props.card.state.removedCards)
			return props.card.state.removedCards.map((card) => card.name);
		if (hasEffect(props.card, "TrackRemovedCardsSubtypes") && props.card.state.removedCards)
			return [...new Set(props.card.state.removedCards.map((card) => card.subtypes).flat())];
		if (hasEffect(props.card, "CogworkGrinder") && props.card.state.removedCards)
			return props.card.state.removedCards.length.toString();
		if (hasEffect(props.card, OnPickDraftEffect.NoteDraftedCards) && props.card.state.cardsDraftedThisRound)
			return props.card.state.cardsDraftedThisRound.toString();
		if (hasEffect(props.card, OnPickDraftEffect.NotePassingPlayer) && props.card.state.passingPlayer)
			return props.card.state.passingPlayer;
		if (props.card.state.cardName) return props.card.state.cardName;
		if (props.card.state.creatureName) return props.card.state.creatureName;
		if (props.card.state.creatureTypes) return props.card.state.creatureTypes;
	}
	return undefined;
});

const notedColors = computed(() => {
	return (props.card.state?.colors?.length ?? 0) > 0 ? props.card.state?.colors : undefined;
});

function toggleZoom(e: Event) {
	e.preventDefault();
	emitter.emit("togglecardpopup", e, props.card);
}

function mouseLeave(e: Event) {
	e.preventDefault();

	displayCardText.value = false;
	document.removeEventListener("keydown", keyDown, { capture: true });
	document.removeEventListener("keyup", keyUp, { capture: true });

	emitter.emit("closecardpopup");

	if (props.card.foil && rootElement.value) {
		document.removeEventListener("mousemove", foilEffect);
		rootElement.value.style.setProperty("--brightness", `100%`);
		rootElement.value.style.setProperty("--foil-initial-top", `-16%`);
		rootElement.value.style.setProperty("--foil-initial-left", `8%`);
		rootElement.value.style.setProperty("--transform-rotation-x", `0`);
		rootElement.value.style.setProperty("--transform-rotation-y", `0`);
	}
}

function mouseEnter() {
	document.addEventListener("keydown", keyDown, { capture: true });
	document.addEventListener("keyup", keyUp, { capture: true });

	if (props.card.foil) {
		document.addEventListener("mousemove", foilEffect);
	}
}

function foilEffect(e: MouseEvent) {
	if (!rootElement.value) return;

	const bounds = rootElement.value.getBoundingClientRect();
	const style = window.getComputedStyle(rootElement.value);
	bounds.width += (parseInt(style.marginLeft) || 0) + (parseInt(style.marginRight) || 0);
	bounds.height += (parseInt(style.marginTop) || 0) + (parseInt(style.marginBottom) || 0);
	const factor = (e.clientX - bounds.left) / bounds.width;
	const factorY = (e.clientY - bounds.top) / bounds.height;
	if (!imageElement.value) {
		document.removeEventListener("mousemove", foilEffect);
		return;
	}
	const imageBounds = imageElement.value.$el.getBoundingClientRect(); // Different from bounds when inside a card column
	const ratio = imageBounds.width / imageBounds.height;
	const rotScale = (v: number) => -20 + 40 * v;
	rootElement.value.style.setProperty("--brightness", `${100 - 50 * (factor - 0.5)}%`);
	rootElement.value.style.setProperty("--transform-rotation-x", `${rotScale(factor)}deg`);
	rootElement.value.style.setProperty("--transform-rotation-y", `${ratio * -rotScale(factorY)}deg`);
	rootElement.value.style.setProperty("--foil-initial-top", `${ratio * (-(160 * factorY) + 53)}%`);
	rootElement.value.style.setProperty("--foil-initial-left", `${-(160 * factor) + 88}%`);
}

function keyDown(event: KeyboardEvent) {
	switch (event.key) {
		case "Alt":
			displayCardText.value = event.altKey;
			getCurrentInstance()?.proxy?.$forceUpdate();
			break;
		default:
			// Ignore this event
			return;
	}
	// We handled it.
	event.stopPropagation();
	event.preventDefault();
}

function keyUp(event: KeyboardEvent) {
	switch (event.key) {
		case "Alt":
			displayCardText.value = event.altKey;
			getCurrentInstance()?.proxy?.$forceUpdate();
			break;
		default:
			// Ignore this event
			return;
	}
	// We handled it.
	event.stopPropagation();
	event.preventDefault();
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "rootElement",
    ref: rootElement,
    class: _normalizeClass(["card", [{ foil: __props.card.foil }, classes.value]]),
    "data-uniqueid": __props.card.uniqueID,
    key: `card-${__props.card.uniqueID}`,
    onContextmenu: toggleZoom,
    onMouseleave: mouseLeave,
    onMouseenter: mouseEnter
  }, [
    _createVNode(CardImage, {
      card: __props.card,
      language: __props.language,
      lazyLoad: __props.lazyLoad,
      displayCardText: displayCardText.value,
      renderCommonBackside: __props.renderCommonBackside,
      ref_key: "imageElement",
      ref: imageElement
    }, null, 8 /* PROPS */, ["card", "language", "lazyLoad", "displayCardText", "renderCommonBackside"]),
    (notes.value || notedColors.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (notes.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                (Array.isArray(notes.value))
                  ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(notes.value, (n) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: n,
                        style: {"white-space":"nowrap"}
                      }, _toDisplayString(n), 1 /* TEXT */))
                    }), 128 /* KEYED_FRAGMENT */))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createTextVNode(_toDisplayString(notes.value), 1 /* TEXT */)
                    ], 64 /* STABLE_FRAGMENT */))
              ], 64 /* STABLE_FRAGMENT */))
            : _createCommentVNode("v-if", true),
          (notedColors.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(notedColors.value, (c) => {
                  return (_openBlock(), _createElementBlock("img", {
                    key: c,
                    class: "mana-icon",
                    src: `img/mana/${c}.svg`
                  }, null, 8 /* PROPS */, _hoisted_4))
                }), 128 /* KEYED_FRAGMENT */))
              ]))
            : _createCommentVNode("v-if", true)
        ]))
      : _createCommentVNode("v-if", true),
    _renderSlot(_ctx.$slots, "default")
  ], 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_1))
}
}

})